import React, { useState } from 'react';
import { toast } from 'sonner';
import { 
  CheckCircle, Clock, Trophy, Gift, Flame, TrendingUp, 
  Home, Utensils, Droplet, Wind, Sun, Moon, Heart, Activity
} from 'lucide-react';
import type { ChoreCategory, CompletedChore, RedeemedReward } from '@/lib/vbs/homeMode';
import {
  CHORES,
  canCompleteChore, getRemainingChoreSlots, getTodayDate,
  calculateLevel, calculateStreak, getChoresByCategory,
  getAffordableRewards, getRewardById,
} from '@/lib/vbs/homeMode';

const iconMap: Record<string, React.ComponentType<Record<string, unknown>>> = {
  Home, Utensils, Droplet, Wind, Sun, Moon, Heart, Activity,
  CheckCircle, Clock, Trophy, Gift, Flame, TrendingUp,
};

interface HomeModeProps {
  onCompleteChore?: (choreId: string, points: number) => void;
  onRedeemReward?: (rewardId: string, points: number) => void;
}

export function HomeMode({ onCompleteChore, onRedeemReward }: HomeModeProps) {
  // Load from localStorage using lazy initialization
  const [completedChores, setCompletedChores] = useState<CompletedChore[]>(() => {
    const stored = localStorage.getItem('homeModeProgress');
    if (stored) {
      const data = JSON.parse(stored);
      return data.completedChores || [];
    }
    return [];
  });
  const [redeemedRewards, setRedeemedRewards] = useState<RedeemedReward[]>(() => {
    const stored = localStorage.getItem('homeModeProgress');
    if (stored) {
      const data = JSON.parse(stored);
      return data.redeemedRewards || [];
    }
    return [];
  });
  const [selectedCategory, setSelectedCategory] = useState<ChoreCategory | 'all'>('all');
  const [showRewards, setShowRewards] = useState(false);

  // Calculate progress
  const totalPoints = completedChores.reduce((sum, c) => sum + c.pointsEarned, 0);
  const redeemedPoints = redeemedRewards.reduce((sum, r) => sum + r.pointsSpent, 0);
  const availablePoints = totalPoints - redeemedPoints;
  
  const { level, progress } = calculateLevel(totalPoints);
  const { current: currentStreak } = calculateStreak(completedChores);
  const remainingSlots = getRemainingChoreSlots(completedChores);

  const saveProgress = (newChores: CompletedChore[], newRewards: RedeemedReward[]) => {
    setCompletedChores(newChores);
    setRedeemedRewards(newRewards);
    localStorage.setItem('homeModeProgress', JSON.stringify({
      completedChores: newChores,
      redeemedRewards: newRewards,
    }));
  };

  const handleCompleteChore = (choreId: string) => {
    const chore = CHORES.find(c => c.id === choreId);
    if (!chore) return;

    // Check if wellness chore (always allowed) or regular chore (respects limit)
    const isWellness = chore.isWellness || chore.category === 'wellness';
    
    if (!isWellness && !canCompleteChore(completedChores)) {
      toast.error('🎯 Daily limit reached! Come back tomorrow for more chores.');
      return;
    }

    const today = getTodayDate();
    
    // Check if already completed today (except wellness which can be done multiple times)
    const alreadyCompleted = completedChores.some(
      c => c.choreId === choreId && c.date === today
    );

    if (alreadyCompleted && !isWellness) {
      toast.info('✅ You already completed this chore today!');
      return;
    }

    const newCompleted: CompletedChore = {
      id: crypto.randomUUID(),
      choreId,
      completedAt: new Date().toISOString(),
      pointsEarned: chore.points,
      date: today,
    };

    const newChores = [...completedChores, newCompleted];
    saveProgress(newChores, redeemedRewards);

    toast.success(`✨ ${chore.name} complete! +${chore.points} points!${isWellness ? ' 🧡' : ''}`);
    onCompleteChore?.(choreId, chore.points);
  };

  const handleRedeemReward = (rewardId: string) => {
    const reward = getRewardById(rewardId);
    if (!reward) return;

    if (availablePoints < reward.pointsCost) {
      toast.error(`❌ Need ${reward.pointsCost - availablePoints} more points!`);
      return;
    }

    if (confirm(`Redeem "${reward.name}" for ${reward.pointsCost} points?`)) {
      const newRedeemed: RedeemedReward = {
        id: crypto.randomUUID(),
        rewardId,
        redeemedAt: new Date().toISOString(),
        pointsSpent: reward.pointsCost,
      };

      const newRewards = [...redeemedRewards, newRedeemed];
      saveProgress(completedChores, newRewards);

      toast.success(`🎉 ${reward.name} redeemed!`);
      onRedeemReward?.(rewardId, reward.pointsCost);
    }
  };

  const filteredChores = selectedCategory === 'all' 
    ? CHORES 
    : getChoresByCategory(selectedCategory);

  const affordableRewards = getAffordableRewards(availablePoints);

  const categories: { id: ChoreCategory | 'all'; name: string; icon: string }[] = [
    { id: 'all', name: 'All', icon: 'Home' },
    { id: 'tidying', name: 'Tidying', icon: 'Home' },
    { id: 'kitchen', name: 'Kitchen', icon: 'Utensils' },
    { id: 'bathroom', name: 'Bathroom', icon: 'Droplet' },
    { id: 'laundry', name: 'Laundry', icon: 'Wind' },
    { id: 'cleaning', name: 'Cleaning', icon: 'Sparkles' },
    { id: 'outdoor', name: 'Outdoor', icon: 'Sun' },
    { id: 'personal', name: 'Personal', icon: 'Heart' },
    { id: 'wellness', name: 'Wellness', icon: 'Activity' },
  ];

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      {/* Header Stats */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-black uppercase tracking-tighter">Home Mode</h2>
            <p className="text-purple-200 text-sm">Build habits, earn rewards!</p>
          </div>
          <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
            <Trophy className="w-5 h-5" />
            <span className="font-black text-xl">{level}</span>
          </div>
        </div>

        {/* Level Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span>Level {level}</span>
            <span>Level {level + 1}</span>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all duration-500"
              style={{ width: `${(progress / 500) * 100}%` }}
            />
          </div>
          <p className="text-xs text-purple-200 mt-1">{progress} / 500 XP to next level</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <TrendingUp className="w-5 h-5 mx-auto mb-1" />
            <p className="text-2xl font-black">{totalPoints}</p>
            <p className="text-xs text-purple-200">Total Points</p>
          </div>
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <Gift className="w-5 h-5 mx-auto mb-1" />
            <p className="text-2xl font-black">{availablePoints}</p>
            <p className="text-xs text-purple-200">Available</p>
          </div>
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <Flame className="w-5 h-5 mx-auto mb-1" />
            <p className="text-2xl font-black">{currentStreak}</p>
            <p className="text-xs text-purple-200">Day Streak</p>
          </div>
        </div>
      </div>

      {/* Daily Limit Alert */}
      {remainingSlots === 0 ? (
        <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-4 text-center">
          <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
          <p className="font-black text-green-800">Daily chores complete! 🎉</p>
          <p className="text-sm text-green-600">Wellness chores still available!</p>
        </div>
      ) : (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 text-center">
          <Clock className="w-8 h-8 text-amber-600 mx-auto mb-2" />
          <p className="font-black text-amber-800">{remainingSlots} chore{remainingSlots > 1 ? 's' : ''} remaining today</p>
          <p className="text-sm text-amber-600">Complete chores to earn points for rewards!</p>
        </div>
      )}

      {/* Category Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map(cat => {
          const Icon = iconMap[cat.icon] || Home;
          return (
            <button
              key={cat.id}
              onClick={() => {
                setSelectedCategory(cat.id);
                setShowRewards(false);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-black text-sm uppercase whitespace-nowrap transition-all ${
                selectedCategory === cat.id && !showRewards
                  ? 'bg-slate-900 text-white shadow-lg'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              {cat.name}
            </button>
          );
        })}
        <button
          onClick={() => setShowRewards(true)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full font-black text-sm uppercase whitespace-nowrap transition-all ${
            showRewards
              ? 'bg-pink-600 text-white shadow-lg'
              : 'bg-white text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Gift className="w-4 h-4" />
          Rewards
        </button>
      </div>

      {/* Chores Grid */}
      {!showRewards && (
        <div className="grid md:grid-cols-2 gap-3">
          {filteredChores.map(chore => {
            const Icon = iconMap[chore.icon] || Home;
            const completedToday = completedChores.some(
              c => c.choreId === chore.id && c.date === getTodayDate()
            );
            const isWellness = chore.isWellness || chore.category === 'wellness';
            const canComplete = isWellness || remainingSlots > 0;

            return (
              <button
                key={chore.id}
                onClick={() => handleCompleteChore(chore.id)}
                disabled={!canComplete || (completedToday && !isWellness)}
                className={`p-4 rounded-2xl border-2 text-left transition-all ${
                  completedToday && !isWellness
                    ? 'bg-green-50 border-green-200 opacity-75'
                    : !canComplete
                    ? 'bg-slate-50 border-slate-200 opacity-50 cursor-not-allowed'
                    : 'bg-white border-slate-200 hover:border-purple-300 hover:shadow-md active:scale-[0.98]'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className={`p-2 rounded-xl ${chore.color}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-black text-purple-600">{chore.points}</span>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">pts</p>
                  </div>
                </div>
                <h3 className="font-black text-slate-900 mb-1">{chore.name}</h3>
                <p className="text-sm text-slate-600 mb-2">{chore.description}</p>
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {chore.estimatedMinutes} min
                  </span>
                  <span className={`px-2 py-0.5 rounded-full font-black uppercase ${
                    chore.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                    chore.difficulty === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {chore.difficulty}
                  </span>
                  {isWellness && (
                    <span className="px-2 py-0.5 rounded-full font-black uppercase bg-teal-100 text-teal-700 text-[10px]">
                      🧡 Wellness
                    </span>
                  )}
                </div>
                {completedToday && !isWellness && (
                  <div className="mt-2 flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-bold text-sm">Completed today!</span>
                  </div>
                )}
                {completedToday && isWellness && (
                  <div className="mt-2 flex items-center gap-2 text-teal-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-bold text-sm">Done! Can do again 🧡</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Rewards Grid */}
      {showRewards && (
        <div className="grid md:grid-cols-2 gap-3">
          {affordableRewards.length === 0 ? (
            <div className="col-span-2 text-center py-12 text-slate-500">
              <Gift className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="font-bold">Keep completing chores to unlock rewards!</p>
            </div>
          ) : (
            affordableRewards.map(reward => {
              const Icon = iconMap[reward.icon] || Gift;
              return (
                <button
                  key={reward.id}
                  onClick={() => handleRedeemReward(reward.id)}
                  className="p-4 rounded-2xl border-2 border-slate-200 bg-white text-left hover:border-pink-300 hover:shadow-md transition-all active:scale-[0.98]"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className={`p-2 rounded-xl ${reward.color}`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xl font-black text-pink-600">{reward.pointsCost} pts</span>
                  </div>
                  <h3 className="font-black text-slate-900 mb-1">{reward.name}</h3>
                  <p className="text-sm text-slate-600">{reward.description}</p>
                  {reward.isRecurring && (
                    <span className="inline-block mt-2 text-[10px] font-bold text-slate-400 uppercase bg-slate-100 px-2 py-0.5 rounded-full">
                      Can redeem multiple times
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
